import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Modal } from './Modal';
import { Boton } from './Boton';
import { Buscar, Check, Chevrones, Equis, Filtro } from './iconos';

/**
 * Buscador con los filtros **adentro**. Portado de Polaris, con la marca de
 * Copathy y sin lo que este proyecto no usa.
 *
 * ── Por qué los filtros van dentro del buscador y no al lado ──
 * Una fila propia para los filtros cuesta unos 56 px de alto en la zona más cara
 * de la pantalla, y esa altura es justo la que decide si la lista cabe sin
 * desplazarse. El botón vive dentro del campo, a la derecha, y lo aplicado se
 * muestra como chips debajo — que además son removibles de a uno.
 *
 * ── Qué se dejó fuera del porte ──
 * El campo de **rango de montos**. Copathy no maneja dinero en la fase 1 (D-003)
 * y traerlo «por si acaso» es código que nadie ejercita y que hay que mantener.
 * Vuelve el día que entren los módulos administrativos.
 */

type Borrador = Record<string, string | undefined>;
type Opcion = { valor: string; texto: string };

type CampoFiltro =
  | { tipo: 'multi'; clave: string; texto: string; opciones: Opcion[] }
  | { tipo: 'booleano'; clave: string; texto: string }
  | { tipo: 'fecha'; claveDesde: string; claveHasta: string; texto: string };

export type SeccionFiltro = { titulo: string; campos: CampoFiltro[] };
/** Casillas sueltas, arriba y fuera del acordeón. Para un sí/no de un vistazo. */
export type CasillaFiltro = { clave: string; texto: string };

type Chip = { texto: string; alQuitar: () => void };

/*
  Los valores viajan como CSV en un solo campo —`activo=si,no`— y no como arreglo,
  porque así entran tal cual en la query de la URL y en el estado de la pantalla
  sin serializar nada.
*/
const aConjunto = (csv?: string) =>
  new Set(
    (csv ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
  );
const aCsv = (s: Set<string>) => (s.size ? [...s].join(',') : undefined);

/** Para buscar «Panamá» escribiendo «panama». */
const normalizar = (s: string) =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

function useEscritorio() {
  const [ok, setOk] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const h = () => setOk(mq.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, []);
  return ok;
}

function contarCampo(c: CampoFiltro, v: Borrador): number {
  if (c.tipo === 'multi') return aConjunto(v[c.clave]).size;
  if (c.tipo === 'booleano') return v[c.clave] ? 1 : 0;
  return (v[c.claveDesde] ? 1 : 0) + (v[c.claveHasta] ? 1 : 0);
}

const contarSeccion = (s: SeccionFiltro, v: Borrador) =>
  s.campos.reduce((n, c) => n + contarCampo(c, v), 0);

const clavesDe = (c: CampoFiltro) =>
  c.tipo === 'fecha' ? [c.claveDesde, c.claveHasta] : [c.clave];

/** ¿El borrador es igual a lo aplicado? Es la guardia de descarte. */
function borradorIgual(secciones: SeccionFiltro[], a: Borrador, b: Borrador): boolean {
  for (const s of secciones) {
    for (const c of s.campos) {
      for (const k of clavesDe(c)) if ((a[k] ?? '') !== (b[k] ?? '')) return false;
    }
  }
  return true;
}

function construirChips(
  secciones: SeccionFiltro[],
  valores: Borrador,
  aplicar: (nuevo: Borrador) => void,
): Chip[] {
  const chips: Chip[] = [];
  for (const s of secciones) {
    for (const c of s.campos) {
      if (c.tipo === 'multi') {
        for (const v of aConjunto(valores[c.clave])) {
          const op = c.opciones.find((o) => o.valor === v);
          chips.push({
            texto: `${c.texto}: ${op?.texto ?? v}`,
            alQuitar: () => {
              const set = aConjunto(valores[c.clave]);
              set.delete(v);
              aplicar({ ...valores, [c.clave]: aCsv(set) });
            },
          });
        }
      } else if (c.tipo === 'booleano') {
        if (valores[c.clave]) {
          chips.push({
            texto: c.texto,
            alQuitar: () => aplicar({ ...valores, [c.clave]: undefined }),
          });
        }
      } else {
        if (valores[c.claveDesde]) {
          chips.push({
            texto: `${c.texto} desde ${valores[c.claveDesde]!}`,
            alQuitar: () => aplicar({ ...valores, [c.claveDesde]: undefined }),
          });
        }
        if (valores[c.claveHasta]) {
          chips.push({
            texto: `${c.texto} hasta ${valores[c.claveHasta]!}`,
            alQuitar: () => aplicar({ ...valores, [c.claveHasta]: undefined }),
          });
        }
      }
    }
  }
  return chips;
}

const Casilla = ({ marcada }: { marcada: boolean }) => (
  <span
    className={`grid h-4 w-4 shrink-0 place-items-center rounded border transition
      ${marcada ? 'border-naranja-500 bg-naranja-500 text-marino-900' : 'border-marino-300'}`}
  >
    {marcada && <Check size={11} />}
  </span>
);

const Insignia = ({ n }: { n: number }) => (
  <span className="grid h-5 min-w-5 place-items-center rounded-full bg-naranja-50 px-1 text-[11px] font-bold text-naranja-700">
    {n}
  </span>
);

/** Con muchas opciones, el checklist necesita su propio buscador. */
const UMBRAL_BUSCADOR = 7;

function CampoMulti({
  campo,
  conTitulo,
  borrador,
  setBorrador,
}: {
  campo: Extract<CampoFiltro, { tipo: 'multi' }>;
  conTitulo: boolean;
  borrador: Borrador;
  setBorrador: React.Dispatch<React.SetStateAction<Borrador>>;
}) {
  const [q, setQ] = useState('');
  const elegidas = aConjunto(borrador[campo.clave]);
  const conBuscador = campo.opciones.length > UMBRAL_BUSCADOR;
  const visibles = q
    ? campo.opciones.filter((o) => normalizar(o.texto).includes(normalizar(q)))
    : campo.opciones;

  const alternar = (v: string) =>
    setBorrador((d) => {
      const s = aConjunto(d[campo.clave]);
      if (s.has(v)) s.delete(v);
      else s.add(v);
      return { ...d, [campo.clave]: aCsv(s) };
    });

  return (
    <div>
      {conTitulo && (
        <span className="mb-1.5 block text-xs font-semibold text-marino-500">{campo.texto}</span>
      )}

      {/* Lo elegido, arriba y removible: sin esto hay que recorrer la lista para
          saber qué está marcado cuando son cuarenta opciones. */}
      {elegidas.size > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {campo.opciones
            .filter((o) => elegidas.has(o.valor))
            .map((o) => (
              <button
                key={o.valor}
                type="button"
                onClick={() => alternar(o.valor)}
                className="inline-flex items-center gap-1 rounded-full bg-naranja-50 px-2 py-0.5 text-xs font-medium text-naranja-700 transition hover:brightness-95"
              >
                {o.texto} <Equis size={11} />
              </button>
            ))}
        </div>
      )}

      {conBuscador && (
        <div className="relative mb-1.5">
          <Buscar
            size={15}
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-marino-400"
          />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Buscar ${campo.texto.toLowerCase()}…`}
            className="w-full rounded-xl border border-marino-200 bg-white py-1.5 pl-8 pr-3 text-sm
              text-marino-900 outline-none transition placeholder:text-marino-400/70 focus:border-marino-700"
          />
        </div>
      )}

      <ul
        className={`overflow-y-auto rounded-xl border border-marino-100 bg-white py-1
          ${conBuscador ? 'max-h-52' : 'max-h-44'}`}
      >
        {visibles.length === 0 && (
          <li className="px-3 py-2 text-sm text-marino-400">Sin coincidencias.</li>
        )}
        {visibles.map((o) => (
          <li key={o.valor}>
            <button
              type="button"
              onClick={() => alternar(o.valor)}
              className="flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-sm text-marino-800
                transition hover:bg-marino-50"
            >
              <Casilla marcada={elegidas.has(o.valor)} />
              {o.texto}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FilaCasilla({
  clave,
  texto,
  borrador,
  setBorrador,
  conBorde,
}: {
  clave: string;
  texto: string;
  borrador: Borrador;
  setBorrador: React.Dispatch<React.SetStateAction<Borrador>>;
  conBorde: boolean;
}) {
  const on = !!borrador[clave];
  return (
    <button
      type="button"
      onClick={() => setBorrador((d) => ({ ...d, [clave]: d[clave] ? undefined : '1' }))}
      className={
        conBorde
          ? `flex w-full items-center gap-2.5 rounded-xl border px-4 py-3 text-left text-sm font-medium transition
             ${on ? 'border-naranja-300 bg-naranja-50 text-naranja-700' : 'border-marino-100 text-marino-800 hover:bg-marino-50'}`
          : 'flex w-full items-center gap-2.5 text-left text-sm text-marino-800'
      }
    >
      <Casilla marcada={on} />
      {texto}
    </button>
  );
}

function Seccion({
  titulo,
  activos,
  children,
}: {
  titulo: string;
  activos: number;
  children: ReactNode;
}) {
  const [abierta, setAbierta] = useState(false);
  return (
    <div className="rounded-2xl border border-marino-100">
      <button
        type="button"
        onClick={() => setAbierta((a) => !a)}
        aria-expanded={abierta}
        className="flex w-full items-center justify-between px-4 py-3"
      >
        <span className="flex items-center gap-2 font-semibold text-marino-800">
          {titulo}
          {activos > 0 && <Insignia n={activos} />}
        </span>
        <Chevrones
          size={16}
          className={`text-marino-400 transition-transform ${abierta ? 'rotate-180' : ''}`}
        />
      </button>
      {abierta && (
        <div className="space-y-4 border-t border-marino-100 px-4 pb-4 pt-4">{children}</div>
      )}
    </div>
  );
}

export function BuscadorFiltros({
  busqueda = '',
  alBuscar,
  guia = 'Buscar…',
  casillas = [],
  secciones = [],
  valores = {},
  alAplicar,
  derecha,
}: {
  busqueda?: string;
  alBuscar?: (v: string) => void;
  guia?: string;
  casillas?: CasillaFiltro[];
  secciones?: SeccionFiltro[];
  valores?: Borrador;
  alAplicar?: (v: Borrador) => void;
  /** En la MISMA fila del buscador. Para el botón de «Registrar», por ejemplo. */
  derecha?: ReactNode;
}) {
  const [abierto, setAbierto] = useState(false);
  const [borrador, setBorrador] = useState<Borrador>(valores);
  const [confirmando, setConfirmando] = useState(false);
  const marco = useRef<HTMLDivElement>(null);
  const esEscritorio = useEscritorio();

  // Las casillas sueltas son booleanos para toda la lógica —chips, conteo,
  // limpiar— pero se pintan aparte, arriba del acordeón.
  const seccionCasillas: SeccionFiltro | null = casillas.length
    ? {
        titulo: '__casillas__',
        campos: casillas.map((c) => ({ tipo: 'booleano' as const, clave: c.clave, texto: c.texto })),
      }
    : null;
  const todas = seccionCasillas ? [seccionCasillas, ...secciones] : secciones;

  const hayFiltros = todas.length > 0;
  const hayCambios = !borradorIgual(todas, borrador, valores);

  const vacio: Borrador = {};
  for (const s of todas) for (const c of s.campos) for (const k of clavesDe(c)) vacio[k] = undefined;

  const chips = construirChips(todas, valores, (n) => alAplicar?.(n));

  const abrir = () => {
    setBorrador(valores);
    setConfirmando(false);
    setAbierto(true);
  };
  const intentarCerrar = () => {
    if (hayCambios) return setConfirmando(true);
    setAbierto(false);
  };
  const descartar = () => {
    setBorrador(valores);
    setConfirmando(false);
    setAbierto(false);
  };

  useEffect(() => {
    if (!abierto || !esEscritorio) return;
    const alApuntar = (e: MouseEvent) => {
      if (marco.current && !marco.current.contains(e.target as Node)) intentarCerrar();
    };
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') intentarCerrar();
    };
    document.addEventListener('mousedown', alApuntar);
    document.addEventListener('keydown', alTeclear);
    return () => {
      document.removeEventListener('mousedown', alApuntar);
      document.removeEventListener('keydown', alTeclear);
    };
    // `hayCambios` entra porque `intentarCerrar` lo lee: sin él, el cierre por
    // clic afuera usaría el valor de cuando se abrió y se saltaría la guardia.
  }, [abierto, esEscritorio, hayCambios]);

  const contenido = (
    <div className="space-y-3">
      {casillas.length > 0 && (
        <div className="space-y-2">
          {casillas.map((c) => (
            <FilaCasilla
              key={c.clave}
              clave={c.clave}
              texto={c.texto}
              borrador={borrador}
              setBorrador={setBorrador}
              conBorde
            />
          ))}
        </div>
      )}
      {secciones.map((s) => (
        <Seccion key={s.titulo} titulo={s.titulo} activos={contarSeccion(s, borrador)}>
          {s.campos.map((c) =>
            c.tipo === 'multi' ? (
              <CampoMulti
                key={c.clave}
                campo={c}
                conTitulo={s.campos.length > 1}
                borrador={borrador}
                setBorrador={setBorrador}
              />
            ) : c.tipo === 'booleano' ? (
              <FilaCasilla
                key={c.clave}
                clave={c.clave}
                texto={c.texto}
                borrador={borrador}
                setBorrador={setBorrador}
                conBorde={false}
              />
            ) : (
              <div key={c.claveDesde} className="grid grid-cols-2 gap-2">
                {(
                  [
                    ['Desde', c.claveDesde],
                    ['Hasta', c.claveHasta],
                  ] as const
                ).map(([rotulo, clave]) => (
                  <label key={clave} className="block">
                    <span className="mb-1 block text-[11px] text-marino-400">{rotulo}</span>
                    <input
                      type="date"
                      value={borrador[clave] ?? ''}
                      onChange={(e) =>
                        setBorrador((d) => ({ ...d, [clave]: e.target.value || undefined }))
                      }
                      className="w-full rounded-xl border border-marino-200 bg-white px-3 py-2 text-sm
                        text-marino-900 outline-none transition focus:border-marino-700"
                    />
                  </label>
                ))}
              </div>
            ),
          )}
        </Seccion>
      ))}
    </div>
  );

  const pie = (
    <>
      <Boton variante="texto" onClick={() => setBorrador(vacio)}>
        Limpiar todo
      </Boton>
      <Boton
        onClick={() => {
          alAplicar?.(borrador);
          setAbierto(false);
        }}
      >
        Aplicar
      </Boton>
    </>
  );

  return (
    <div ref={marco} className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-48 flex-1">
          <Buscar
            size={18}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-marino-400"
          />
          <input
            value={busqueda}
            onChange={(e) => alBuscar?.(e.target.value)}
            placeholder={guia}
            className={`w-full rounded-2xl border border-marino-200 bg-white py-3 pl-10 text-marino-900
              outline-none transition placeholder:text-marino-400/70
              hover:border-marino-400 focus:border-marino-700 focus:ring-4 focus:ring-marino-700/10
              ${hayFiltros ? 'pr-12' : 'pr-4'}`}
          />

          {hayFiltros && (
            <button
              type="button"
              onClick={() => (abierto ? intentarCerrar() : abrir())}
              aria-expanded={abierto}
              title="Filtros"
              className={`absolute right-2 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center
                rounded-xl transition
                ${
                  abierto
                    ? 'bg-naranja-500 text-marino-900'
                    : chips.length > 0
                      ? 'text-marino-700 hover:bg-marino-50'
                      : 'text-marino-400 hover:bg-marino-50 hover:text-marino-700'
                }`}
            >
              <Filtro size={18} />
              {chips.length > 0 && !abierto && (
                <span className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-naranja-500 px-1 text-[10px] font-bold text-marino-900">
                  {chips.length}
                </span>
              )}
            </button>
          )}

          {hayFiltros && esEscritorio && abierto && (
            <div className="absolute right-0 top-full z-40 mt-2 flex max-h-[75vh] w-88 max-w-[calc(100vw-2rem)] flex-col rounded-2xl border border-marino-100 bg-white shadow-[var(--sombra-lg)]">
              <div className="flex items-center justify-between border-b border-marino-100 px-4 py-3">
                <span className="font-bold text-marino-800">Filtros</span>
                <button
                  type="button"
                  onClick={intentarCerrar}
                  aria-label="Cerrar filtros"
                  className="rounded-lg p-1 text-marino-400 transition hover:bg-marino-50 hover:text-marino-700"
                >
                  <Equis size={18} />
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto p-4">{contenido}</div>
              <div className="flex items-center justify-between gap-2 border-t border-marino-100 px-4 py-3">
                {pie}
              </div>

              {/* Guardia de descarte. Va DENTRO del panel y no como confirm del
                  navegador: lo que se pierde son filtros a medio armar, no datos,
                  y un cuadro del sistema para eso es desproporcionado. */}
              {confirmando && (
                <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-marino-900/40 p-5 backdrop-blur-sm">
                  <div className="w-full max-w-64 rounded-2xl bg-white p-4 shadow-[var(--sombra-lg)]">
                    <h3 className="font-bold text-marino-800">Filtros sin aplicar</h3>
                    <p className="mt-0.5 text-sm text-marino-500">
                      Si sales ahora se descartan los cambios que todavía no aplicaste.
                    </p>
                    <div className="mt-3 flex justify-end gap-2">
                      <Boton
                        variante="secundario"
                        className="px-3 py-1.5 text-sm"
                        onClick={() => setConfirmando(false)}
                      >
                        Seguir
                      </Boton>
                      <Boton className="px-3 py-1.5 text-sm" onClick={descartar}>
                        Descartar
                      </Boton>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {derecha}
      </div>

      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {chips.map((c, i) => (
            <button
              key={i}
              type="button"
              onClick={c.alQuitar}
              className="inline-flex items-center gap-1 rounded-full bg-naranja-50 px-2.5 py-1 text-xs
                font-medium text-naranja-700 transition hover:brightness-95"
            >
              {c.texto} <Equis size={12} />
            </button>
          ))}
          <button
            type="button"
            onClick={() => alAplicar?.(vacio)}
            className="ml-1 text-xs text-marino-400 transition hover:text-marino-700 hover:underline"
          >
            Limpiar todo
          </button>
        </div>
      )}

      {/* En móvil el panel flotante no cabe: se usa la hoja a pantalla completa,
          que es la misma regla de «modal en escritorio, página en móvil». */}
      {hayFiltros && !esEscritorio && (
        <Modal
          titulo="Filtros"
          abierto={abierto}
          hayCambios={hayCambios}
          alCerrar={() => setAbierto(false)}
          pie={pie}
        >
          {contenido}
        </Modal>
      )}
    </div>
  );
}
